import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { AIGenerator } from "../components/AIGenerator";
import { ImageStudio } from "../components/ImageStudio";
import { useConfirm } from "../components/useConfirm";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Plus, Pencil, Trash2, Copy, Star, Quote, BookMarked } from "lucide-react";

const THEMES = ["Ambientazioni naturali", "Riflessioni interiori", "Psicologia dei personaggi", "Retroscena della scrittura"];

export function Book() {
  const [imgPrompt, setImgPrompt] = useState("");
  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6" data-testid="book-view">
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <ReviewsPanel />
        <ExcerptsPanel />
      </div>
      <AIGenerator kind="book_storytelling" sector="book"
        title="Suggeritore: post & Reel dal romanzo"
        subtitle="Citazioni evocative, storytelling emotivo e retroscena della scrittura"
        placeholder="Es. citazione sul bosco al tramonto…" />
      <ImageStudio prompt={imgPrompt} setPrompt={setImgPrompt} sector="book" />
    </div>
  );
}

function ReviewsPanel() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ reader: "", text: "", rating: 5, source: "" });
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/reviews").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ reader: "", text: "", rating: 5, source: "" }); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...r }); setOpen(true); };
  const save = async () => {
    if (!form.text.trim()) { toast.error("Inserisci il testo della recensione"); return; }
    if (editing) await api.put(`/reviews/${editing.id}`, form); else await api.post("/reviews", form);
    toast.success("Salvato"); setOpen(false); load();
  };
  const remove = async (r) => {
    if (!(await confirm({ title: "Eliminare la recensione?", confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/reviews/${r.id}`); toast.success("Eliminata"); load();
  };
  const copyForPost = (r) => {
    const t = `"${r.text}"${r.reader ? `\n\n— ${r.reader}` : ""}${r.source ? ` (${r.source})` : ""}`;
    navigator.clipboard.writeText(t); toast.success("Copiato per il post social");
  };

  return (
    <div className="glass rounded-2xl p-5" style={{ borderTop: "3px solid #3B82F6" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><Star className="h-5 w-5 text-blue-400" /><h3 className="font-display font-bold">Recensioni & citazioni</h3></div>
        <button data-testid="new-review-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-400 transition-colors"><Plus className="h-4 w-4" /> Nuova</button>
      </div>
      <div className="space-y-2.5">
        {list.length === 0 && <p className="text-sm text-slate-500">Nessuna recensione ancora. Aggiungi le citazioni dei lettori per trasformarle in post.</p>}
        {list.map((r) => (
          <div key={r.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 group" data-testid={`review-${r.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="flex items-center gap-0.5 mb-1">
                  {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-3 w-3" style={{ fill: i < r.rating ? "#3B82F6" : "transparent", color: "#3B82F6" }} />)}
                </div>
                <p className="text-sm text-slate-200 italic">“{r.text}”</p>
                {(r.reader || r.source) && <p className="text-xs text-slate-500 mt-1">— {r.reader}{r.source ? ` · ${r.source}` : ""}</p>}
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button data-testid={`copy-review-${r.id}`} onClick={() => copyForPost(r)} title="Copia per post social" className="p-1.5 rounded-lg hover:bg-blue-500/20"><Copy className="h-3.5 w-3.5 text-blue-300" /></button>
                <button onClick={() => openEdit(r)} className="p-1.5 rounded-lg hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                <button onClick={() => remove(r)} className="p-1.5 rounded-lg hover:bg-rose-500/20 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="review-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica recensione" : "Nuova recensione"}</DialogTitle>
            <DialogDescription className="text-slate-400">Salva la citazione di un lettore.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <textarea data-testid="review-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Testo della recensione / citazione" rows={4} className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="review-reader-input" value={form.reader} onChange={(e) => setForm({ ...form, reader: e.target.value })} placeholder="Lettore" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
              <input data-testid="review-source-input" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} placeholder="Fonte (Amazon, IG…)" className="w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none" />
            </div>
            <div>
              <label className="text-xs text-slate-400">Valutazione</label>
              <div className="flex gap-1 mt-1">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => setForm({ ...form, rating: n })} className="p-1"><Star className="h-5 w-5" style={{ fill: n <= form.rating ? "#3B82F6" : "transparent", color: "#3B82F6" }} /></button>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10">Annulla</button>
            <button data-testid="save-review-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-400">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUI}
    </div>
  );
}

function ExcerptsPanel() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: "", theme: THEMES[0], text: "" });
  const { confirm, ConfirmUI } = useConfirm();

  const load = () => api.get("/excerpts").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ title: "", theme: THEMES[0], text: "" }); setOpen(true); };
  const openEdit = (x) => { setEditing(x); setForm({ ...x }); setOpen(true); };
  const save = async () => {
    if (!form.title.trim()) { toast.error("Inserisci un titolo"); return; }
    if (editing) await api.put(`/excerpts/${editing.id}`, form); else await api.post("/excerpts", form);
    toast.success("Salvato"); setOpen(false); load();
  };
  const remove = async (x) => {
    if (!(await confirm({ title: "Eliminare l'estratto?", confirmLabel: "Elimina", variant: "danger" }))) return;
    await api.delete(`/excerpts/${x.id}`); toast.success("Eliminato"); load();
  };
  const copy = (x) => { navigator.clipboard.writeText(x.text); toast.success("Estratto copiato"); };

  return (
    <div className="glass rounded-2xl p-5" style={{ borderTop: "3px solid #3B82F6" }}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2"><BookMarked className="h-5 w-5 text-blue-400" /><h3 className="font-display font-bold">Archivio estratti & temi</h3></div>
        <button data-testid="new-excerpt-button" onClick={openNew} className="flex items-center gap-1.5 text-sm px-3 py-2 rounded-xl bg-blue-500 text-white font-semibold hover:bg-blue-400 transition-colors"><Plus className="h-4 w-4" /> Nuovo</button>
      </div>
      <div className="space-y-2.5">
        {list.length === 0 && <p className="text-sm text-slate-500">Archivia estratti del romanzo per riusarli nei contenuti.</p>}
        {list.map((x) => (
          <div key={x.id} className="p-3 rounded-xl bg-white/[0.03] border border-white/5 group" data-testid={`excerpt-${x.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-semibold text-sm">{x.title}</p>
                <span className="inline-block text-[11px] px-2 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30 mt-1">{x.theme}</span>
                <p className="text-xs text-slate-400 mt-1.5 line-clamp-3">{x.text}</p>
              </div>
              <div className="flex flex-col gap-1 shrink-0">
                <button onClick={() => copy(x)} className="p-1.5 rounded-lg hover:bg-blue-500/20"><Copy className="h-3.5 w-3.5 text-blue-300" /></button>
                <button onClick={() => openEdit(x)} className="p-1.5 rounded-lg hover:bg-white/10 opacity-0 group-hover:opacity-100 transition-opacity"><Pencil className="h-3.5 w-3.5 text-slate-400" /></button>
                <button onClick={() => remove(x)} className="p-1.5 rounded-lg hover:bg-rose-500/20 opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="glass border-white/10 max-w-md" data-testid="excerpt-modal">
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? "Modifica estratto" : "Nuovo estratto"}</DialogTitle>
            <DialogDescription className="text-slate-400">Testo e tema del romanzo.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <input data-testid="excerpt-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titolo / capitolo" className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none" />
            <div>
              <label className="text-xs text-slate-400">Tema</label>
              <select data-testid="excerpt-theme-select" value={form.theme} onChange={(e) => setForm({ ...form, theme: e.target.value })} className="w-full mt-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm focus:outline-none">
                {THEMES.map((t) => <option key={t} value={t} className="bg-slate-900">{t}</option>)}
              </select>
            </div>
            <textarea data-testid="excerpt-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Estratto…" rows={5} className="w-full bg-white/5 border border-white/10 rounded-xl px-3.5 py-2.5 text-sm focus:outline-none resize-none" />
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)} className="px-4 py-2.5 rounded-xl bg-white/5 border border-white/10 text-sm hover:bg-white/10">Annulla</button>
            <button data-testid="save-excerpt-button" onClick={save} className="px-4 py-2.5 rounded-xl bg-blue-500 text-white text-sm font-semibold hover:bg-blue-400">Salva</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {ConfirmUI}
    </div>
  );
}
